import { Inject, Injectable } from '@angular/core'
import {
  catchError,
  distinctUntilChanged,
  filter,
  last,
  map,
  pairwise,
  pluck,
  shareReplay,
  startWith,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs/operators'
import { CollectionsService } from '../libs/collections/services/collections.service'
import { combineLatest, iif, merge, Observable, of, Subject } from 'rxjs'
import { CollectionsAction, CollectionsActionType } from './collections.models'
import { CollectionFloApiResponse, NodesFloApiResponse } from 'src/io/models/http/collections'
import { Meta, PreFacetedTypes } from '../shared/models/api-response.model'
import { Node } from 'src/io/models/node'
import { RouterService } from '../singleton-services/router.service'
import { CollectionChildNodeType, CollectionChildNodeTypeGuard } from 'src/io/models/collection-child-node-type'
import { EventViewFilter } from 'src/io/models/event-view-filter'
import { CollectionRequestParams } from 'src/io/models/http/collection-request-params'
import { TabComponentProps } from '../libs/collections/components/collection-header/collection-header.interface'
import { CollectionChildNodesRequestParams } from 'src/io/models/http/collection-child-nodes-request-params'
import { VerticalService } from '../singleton-services/vertical.service'
import { SiteIds } from '../shared/models/site.model'
import { HttpErrorResponse } from '@angular/common/http'
import { LOGGER_SERVICE } from '../logger/logger.config'
import { LoggerService } from '../logger/logger.interface'
import { ApiNodeTypes, NonApiNodeTypes } from 'src/io/models/collection'
import { UpdateContentTargetingService } from '../shared/ads/advertising-service/update-content-targeting.service'
import { PersonNode } from '../shared/models/node.model'
import { ExperimentationService } from '../shared/experimentation/experimentation.service'
import {
  MultipleSectionsViewModel,
  SectionModel,
  ViewComponent,
  ViewComponentModels
} from '@flocasts/experience-service-types'
import { isRight } from 'fp-ts/Either'
import { literal, string, type, union } from 'io-ts'
import { ExperienceCollectionDetailSupportedTabs } from '../app.config'
import { SearchFiltersService } from '../singleton-services/search-filters.service'
import { getIdFromSlug } from '../shared/utility-functions/slug.utility'

export const defaultEventView = 'Completed'

export type OptionalCollectionSlug = {
  collectionSlug?: string | null
}
type NodeWithCollectionSlug = Node & OptionalCollectionSlug

export const isPerson = (node: unknown): node is PersonNode => {
  return (
    (node as PersonNode).type === 'person' &&
    (node as PersonNode).gender !== undefined &&
    (node as PersonNode).name !== undefined
  )
}

/*
 * Handle data fetching for a single collection with child nodes.
 * @deprecated
 * New bff-powered entity collections/hub initiative will replace this module.
 * See /experiences/web/entity-hub BFF endpoint
 */
@Injectable()
export class CollectionFacade {
  constructor(
    private readonly collectionsService: CollectionsService,
    private readonly routerService: RouterService,
    private readonly vs: VerticalService,
    private readonly updateContentTargeting: UpdateContentTargetingService,
    private readonly es: ExperimentationService,
    @Inject(LOGGER_SERVICE) private readonly logger: LoggerService,
    private readonly searchFiltersService: SearchFiltersService
  ) {}

  public readonly experiments$ = of([])

  public readonly action$ = new Subject<CollectionsAction>()

  private readonly moreNodesSelected$ = this.action$.pipe(
    filter(action => action.type === CollectionsActionType.MoreNodesSelected)
  )

  private readonly collectionId$ = this.routerService.route$.pipe(
    pluck('params'),
    pluck('collectionId'),
    filter(Boolean),
    map(String),
    map(id => getIdFromSlug(id)),
    map(String)
  )

  private readonly collectionTag$ = this.routerService.route$.pipe(
    pluck('params'),
    pluck('collectionTag'),
    filter(Boolean),
    map(String)
  )

  private readonly collectionNodeId$ = this.routerService.route$.pipe(
    pluck('params'),
    pluck('nodeId'),
    filter(Boolean),
    map(String),
    map(id => getIdFromSlug(id)),
    map(String)
  )

  public readonly collectionIdentifier$ = merge(
    this.collectionId$.pipe(map(collectionId => ({ collectionId }))),
    this.collectionTag$.pipe(map(collectionTag => ({ collectionTag }))),
    this.collectionNodeId$.pipe(map(nodeId => ({ nodeId })))
  )

  private readonly selectedNodeId$ = this.routerService.queryParams$.pipe(
    map(params => (params || {}).playing),
    distinctUntilChanged()
  )

  private readonly eventViewFilter$: Observable<EventViewFilter> = this.routerService.queryParams$.pipe(
    map(params => (params || {}).view || defaultEventView),
    distinctUntilChanged()
  )

  private readonly collectionRequestParams$: Observable<CollectionRequestParams> = this.routerService.route$.pipe(
    map(route => route.data),
    map((data: any) => data?.collection?.params),
    shareReplay({
      bufferSize: 1,
      refCount: true
    })
  )

  private readonly collectionApiResponse$: Observable<CollectionFloApiResponse> = this.routerService.route$.pipe(
    map(route => route.data),
    map((data: any) => data?.collection?.collectionRes),
    filter(res => !!res),
    shareReplay({
      bufferSize: 1,
      refCount: true
    })
  )

  public readonly collection$ = combineLatest([this.collectionApiResponse$, this.collectionIdentifier$]).pipe(
    map(([response, collectionIdentifier]) => ({
      collectionTypeParam: Object.values(collectionIdentifier)[0],
      ...response.data
    })),
    distinctUntilChanged((a, b) => a.id === b.id && a.tag === b.tag && a.nodeId === b.nodeId)
  )

  public nodeWithShareableLink$ = combineLatest([this.collectionApiResponse$, this.vs.coreSiteUrl$]).pipe(
    map(([collection, coreSiteUrl]) => {
      return {
        ...collection.data.original_entity,
        // shareable_link typically comes back null from core api, so we build it here
        shareable_link: `${coreSiteUrl}${collection.data.slug_uri}`
      }
    })
  )

  private readonly collectionDefaultNodeType$: Observable<CollectionChildNodeType> = this.collection$.pipe(
    map(
      collection =>
        (collection &&
          collection.node_associations &&
          collection.node_associations[0] &&
          // @ts-expect-error
          (collection.node_associations[0].type as CollectionChildNodeType)) ||
        ('event' as CollectionChildNodeType)
    )
  )

  private readonly baseSelectedTab$: Observable<CollectionChildNodeType> = combineLatest([
    this.routerService.route$.pipe(map(route => route.params.tab)),
    this.collectionDefaultNodeType$
  ]).pipe(
    map(([tabFromUrl, collectionDefaultNodeType]) => {
      if (CollectionChildNodeTypeGuard.is(tabFromUrl)) {
        return tabFromUrl
      } else {
        return collectionDefaultNodeType
      }
    }),
    distinctUntilChanged(),
    shareReplay({
      bufferSize: 1,
      refCount: true
    })
  )

  private readonly statxSelectedTab$: Observable<CollectionChildNodeType> = this.routerService.route$
    .pipe(map(route => route.params.tab))
    .pipe(
      map(tabFromUrl => {
        if (CollectionChildNodeTypeGuard.is(tabFromUrl)) {
          return tabFromUrl
        } else {
          return ApiNodeTypes.result
        }
      })
    )

  public readonly baseTabs$ = combineLatest([this.collectionApiResponse$, this.baseSelectedTab$]).pipe(
    map(([response, selectedTab]) => getTabsFromCollectionResponse(response, selectedTab)),
    // Now that we have re-categorized leagues, collections are prematurely displaying a Leagues tab.
    // This hides the leagues tab, it will be displayed on new Entity Collections when ready.
    map(tabs => tabs.filter(tab => tab.value !== 'league'))
  )

  private isGrappling$: Observable<boolean> = this.vs.siteId$.pipe(map(x => x === SiteIds.FLOGRAPPLING))

  private personNodeId$ = this.collectionApiResponse$.pipe(
    map(x => x.data.metadata_filters?.filter(y => y.type === 'person')),
    map(x => !!x && x.length > 0 && x[0].id)
  )

  /**
   * StatX powered results tab for FloGrappling Persons only
   */
  private statxGrapplingResults$ = combineLatest([this.isGrappling$, this.personNodeId$]).pipe(
    switchMap(([isGrappling, coreNodeId]) => {
      if (isGrappling && coreNodeId) {
        return this.collectionsService.getStatXResultsByCoreNodeId(coreNodeId, 'grappling').pipe(
          catchError((err: any) => {
            if (err instanceof HttpErrorResponse) {
              if (err.status < 500) this.logger.warn('Collection Facade: StatX Grappling Result Warning', err.error)
              if (err.status >= 500) {
                this.logger.error('Collection Facade: StatX Grappling Result HttpErrorResponse', err.error)
              }
            } else {
              this.logger.error('Collection Facade: StatX Grappling Result Error', err)
            }
            return of(undefined)
          })
        )
      }
      return of(undefined)
    })
  )

  /**
   * Experienced Powered results tab for FloGrappling & FloWrestling Persons only
   *
   * If not a valid person NodeId or the person NodeId does not contain statX results, return undefined or false.
   *
   * If the NodeId is valid, and it has results, but search (or other) filters return no results, return response with
   * included MessageModel
   *
   */
  public personResultsTab$ = combineLatest([this.personNodeId$, this.routerService.queryParams$, this.vs.siteId$]).pipe(
    switchMap(([coreNodeId, queryParams, siteId]) => {
      if (coreNodeId && (siteId === SiteIds.FLOGRAPPLING || siteId === SiteIds.FLOWRESTLING)) {
        return this.collectionsService
          .getExperienceCollectionDetailTab(coreNodeId, ExperienceCollectionDetailSupportedTabs.Results, queryParams)
          .pipe(tap(() => this.searchFiltersService.isFilterOrSearchLoading$.next(false)))
      }
      return of(undefined)
    }),
    map(
      // If 404 from search results, there will still be SearchModel in the sections array, so length will be longer than 1.
      // If 404 from a bad node Id or from a node Id with no statx results, there will only be MessageModel, so length will equal 1
      (response: MultipleSectionsViewModel) => {
        if (response && response.sections && response.sections.length > 1) {
          return response.sections
        }
        return undefined
      }
    ),
    distinctUntilChanged(),
    shareReplay({
      bufferSize: 1,
      refCount: true
    })
  )

  /**
   * @deprecated
   * Populate subheader for grappling- the Win Loss Draw record fetched from StatX
   */
  public readonly statxSubHeader$: Observable<string | undefined> = this.statxGrapplingResults$.pipe(
    map(results => {
      if (results && 'aggregations' in results) {
        const GRAPPLING_ATHLETE_RESULT_AGG_WLD_RECORD = 'wlRecord'
        const resultRow = results.aggregations.find(y => y.key === GRAPPLING_ATHLETE_RESULT_AGG_WLD_RECORD)?.rows[0]
        return resultRow ? `${resultRow.wins.text}-${resultRow.losses.text}-${resultRow.draws.text} (W-L-D)` : undefined
      } else {
        return undefined
      }
    })
  )

  public readonly statxTabs$ = combineLatest([this.baseTabs$, this.statxSelectedTab$]).pipe(
    map(([baseTabs, selectedTab]) => {
      const resultsTab: TabComponentProps = {
        active: selectedTab === ApiNodeTypes.result,
        count: 1,
        displayName: 'Results',
        route: `./${ApiNodeTypes.result}`,
        value: NonApiNodeTypes.grappling
      }

      return [
        resultsTab,
        ...baseTabs
          .filter(tab => tab.value !== ApiNodeTypes.result)
          .map(x => ({ ...x, active: !resultsTab.active && x.active }))
      ]
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true
    })
  )

  public readonly tabs$ = this.personResultsTab$.pipe(switchMap(x => iif(() => !!x, this.statxTabs$, this.baseTabs$)))

  public readonly selectedTab$ = this.personResultsTab$.pipe(
    switchMap(x => {
      if (!!x) {
        return this.statxSelectedTab$
      }

      return this.baseSelectedTab$
    })
  )

  private readonly nodesRequestParams$ = combineLatest([
    this.collection$.pipe(switchMap(() => this.collectionRequestParams$)),
    this.eventViewFilter$,
    this.selectedTab$
  ]).pipe(
    withLatestFrom(this.selectedNodeId$),
    // tslint:disable-next-line
    map(([[collectionParams, view, type], include_node]) => ({
      ...collectionParams,
      view,
      include_node,
      type
    }))
  )

  // Update parent component with new ad targeting for selected node
  private updateNodeAdTargeting(
    selectedNode: Pick<NodeWithCollectionSlug, 'aggregated_category_slugs' | 'aggregated_node_ids'> | undefined
  ): void {
    this.updateContentTargeting.updateContentTargeting({
      contentIds: selectedNode && selectedNode.aggregated_node_ids,
      categorySlugs: selectedNode && selectedNode.aggregated_category_slugs
    })
  }

  private readonly nodesPaginatedRequest$ = this.nodesRequestParams$.pipe(
    withLatestFrom(this.collectionApiResponse$),
    startWith([]),
    pairwise(),
    filter(([[oldRequestParams, oldCollectionResponse], [newRequestParams, newCollectionResponse]]) => {
      const responseCollectionId = (newCollectionResponse.data || {}).id
      const responseCollectionTag = (newCollectionResponse.data || {}).tag
      const responseCollectionNodeId = (newCollectionResponse.data || {}).original_entity?.id
      const newCollectionId = (newRequestParams || {}).collectionId
      const newCollectionTag = (newRequestParams || {}).collectionTag
      const newCollectionNodeId = (newRequestParams || {}).nodeId
      const paramsMatchCollectionResponse =
        (!!newCollectionId && `${responseCollectionId}` === `${newCollectionId}`) ||
        (!!newCollectionTag && `${responseCollectionTag}` === `${newCollectionTag}`) ||
        (!!newCollectionNodeId && `${responseCollectionNodeId}` === `${newCollectionNodeId}`)
      return paramsMatchCollectionResponse
    }),
    map(([[oldRequestParams, oldCollectionResponse], [newRequestParams, newCollectionResponse]]) => {
      const newCollectionType = (newRequestParams || {}).type
      const defaultCollectionType = ((newCollectionResponse.data?.node_associations || [])[0] || {}).type
      // @ts-expect-error
      const isDefaultCollectionType = newCollectionType === defaultCollectionType
      const isEventView = newCollectionType === 'event'
      const initialResponse =
        !isDefaultCollectionType || isEventView
          ? undefined
          : {
              meta: newCollectionResponse.meta as Meta,
              data: newCollectionResponse.data.node_associations as ReadonlyArray<Node>
            }
      // Get collection nodes by collection id: collections/{id}/nodes
      if (newRequestParams.collectionId) {
        return this.collectionsService.getCollectionNodesById(
          newRequestParams.collectionId,
          newRequestParams,
          initialResponse
        )
      }
      // Get collection nodes by nodeId: collections/from-node/{nodeId}/nodes
      if (newRequestParams.nodeId && !isNaN(Number(newRequestParams.nodeId))) {
        return this.collectionsService.getCollectionNodesByNodeId(
          Number(newRequestParams.nodeId),
          newRequestParams,
          initialResponse
        )
      }

      // Get collection nodes by collectionTag: collections/tag/{slug}
      return this.collectionsService.getCollectionNodesByTag(
        newRequestParams.collectionTag as string,
        newRequestParams as CollectionChildNodesRequestParams,
        initialResponse
      )
    }),
    shareReplay({
      bufferSize: 1,
      refCount: true
    })
  )

  private collectionSlug$ = this.collection$.pipe(map(collection => collection.slug))

  public readonly nodes$ = this.nodesPaginatedRequest$.pipe(
    switchMap(paginatedRequest => combineLatest([paginatedRequest.allPageDataArray$, this.collectionSlug$])),
    map(([allPageDataArray, collectionSlug]) =>
      supplementNodesWithCollectionSlug(allPageDataArray || [], collectionSlug)
    ),
    shareReplay({
      refCount: true,
      bufferSize: 1
    })
  )

  public readonly currentNodesParams$ = combineLatest([
    this.nodesRequestParams$,
    this.nodesPaginatedRequest$.pipe(switchMap(paginatedRequest => paginatedRequest.pagination$))
  ]).pipe(
    map(([nodesRequestParams, pagination]) => ({
      ...nodesRequestParams,
      ...pagination
    }))
  )

  public relatedCollections$: Observable<NodesFloApiResponse> = this.routerService.route$.pipe(
    map(route => route.data),
    map((data: any) => data?.collection?.relatedCollections),
    shareReplay({
      bufferSize: 1,
      refCount: true
    })
  )

  public readonly selectedNode$: Observable<Node | undefined> = combineLatest([this.selectedNodeId$, this.nodes$]).pipe(
    map(([nodeId, nodes]) => nodes.find(node => `${node.id}` === `${nodeId}`)),
    tap(selectedNode => this.updateNodeAdTargeting(selectedNode)),
    shareReplay(1)
  )

  public readonly getNextPage = this.moreNodesSelected$
    .pipe(withLatestFrom(this.nodesPaginatedRequest$))
    .subscribe(([, paginatedRequest]) => {
      paginatedRequest.goToNextPage()
    })

  public readonly destroyPaginatedRequests = this.nodesPaginatedRequest$
    .pipe(
      pairwise(),
      tap(([previousPaginatedRequest]) => previousPaginatedRequest.destroy()),
      last()
    )
    .subscribe({
      next: paginatedRequests => paginatedRequests.forEach(paginatedRequest => paginatedRequest.destroy())
    })
}

export const getTabsFromCollectionResponse = (
  response: CollectionFloApiResponse,
  selectedTab: unknown
): ReadonlyArray<TabComponentProps> => {
  const extra =
    response.meta &&
    response.meta.extra &&
    (response.meta.extra[1] as {
      pre_faceted_types: ReadonlyArray<PreFacetedTypes>
      pre_faceted_display_names: Record<string, string>
    })

  const isVarsity = response.meta.sites?.includes(SiteIds.VARSITY)
  if (isVarsity && extra) {
    extra.pre_faceted_display_names.ranking = 'Standings'
  }

  const preFacetedTypes = (extra && extra.pre_faceted_types) || []
  const preFacetedDisplayNames = (extra && extra.pre_faceted_display_names) || {}

  const collection = response.data
  const nodes = collection.node_associations
  const defaultTab = CollectionChildNodeTypeGuard.is(selectedTab) ? selectedTab : nodes && nodes[0] && nodes[0].type
  return preFacetedTypes.map(tab => ({
    ...tab,
    displayName: !!preFacetedDisplayNames[tab.value] ? preFacetedDisplayNames[tab.value] : tab.value,
    route: `./${tab.value}`,
    active: tab.value === defaultTab
  }))
}

const supplementNodesWithCollectionSlug = (nodes: Node[], collectionSlug?: string | null): NodeWithCollectionSlug[] => {
  return [...nodes.map(node => ({ ...node, collectionSlug }))]
}

/**
 * @deprecated, this is no longer used but entangled in this component
 * and too risky to untangle.
 * This List component will soon be deprecated itself
 * in favor of the new fully BFF-driven athlete pages.
 */
export interface AthleteResults {
  data: any
  aggregations: ReadonlyArray<any>
}

/**
 * Decoder & validator for Experience Service powered Results. Temporarily, until the new Teams/Athletes profile page is created,
 * we will conditionally get data for Results tab from either statx directly or from the Experience Service.
 *
 * This is temporary while both data sources exist side by side in the component
 *
 * @param resultsTabData Either Experience Service Results or direct StatX Results data
 */
export function isExperienceStatX(
  resultsTabData:
    | ViewComponent
    | SectionModel<ViewComponentModels>
    | (ViewComponent | SectionModel<ViewComponentModels>)[]
    | AthleteResults
) {
  const resultData = Array.isArray(resultsTabData) ? resultsTabData[0] : resultsTabData

  return isRight(
    type({
      id: string,
      type: union([literal('collection'), literal('message'), literal('search'), literal('text')]),
      style: string
    }).decode(resultData)
  )
}
